import type { DebtPlan, DebtResponse, DebtsResponse, NudgeResponse } from '@nargus/shared';
import { download, request, upload } from '../http';

export interface CreateDebtInput {
  organizationId: string;
  debtorId?: string;
  creditor: string;
  reference?: string;
  iban?: string;
  note?: string;
  totalCents: number;
  plan: DebtPlan;
  dueAt?: string;
  installmentCents?: number;
  dueDay?: number;
}

export interface UpdateDebtInput {
  organizationId: string;
  debtId: string;
  debtorId?: string;
  creditor?: string;
  reference?: string | null;
  iban?: string | null;
  note?: string | null;
  totalCents?: number;
  plan?: DebtPlan;
  dueAt?: string | null;
  installmentCents?: number | null;
  dueDay?: number | null;
}

export interface DebtRef {
  organizationId: string;
  debtId: string;
}

export interface AddPaymentInput extends DebtRef {
  amountCents: number;
  paidAt?: string;
  receipt?: File;
}

export interface PaymentRef extends DebtRef {
  paymentId: string;
}

export interface UpdatePaymentInput extends PaymentRef {
  amountCents?: number;
  paidAt?: string;
  receipt?: File;
}

export interface DebtFileInput extends DebtRef {
  file: File;
}

export interface DebtFileRef extends DebtRef {
  attachmentId: string;
}

function paymentBody(input: {
  amountCents?: number;
  paidAt?: string;
  receipt?: File;
}): FormData {
  const body = new FormData();
  if (input.amountCents !== undefined) body.append('amountCents', String(input.amountCents));
  if (input.paidAt) body.append('paidAt', input.paidAt);
  if (input.receipt) body.append('receipt', input.receipt);
  return body;
}

export const debtResource = {
  list: (organizationId: string): Promise<DebtsResponse> =>
    request(`/organizations/${organizationId}/debts`),

  create: ({ organizationId, ...body }: CreateDebtInput): Promise<DebtResponse> =>
    request(`/organizations/${organizationId}/debts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: ({ organizationId, debtId, ...body }: UpdateDebtInput): Promise<DebtResponse> =>
    request(`/organizations/${organizationId}/debts/${debtId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  remove: ({ organizationId, debtId }: DebtRef): Promise<void> =>
    request(`/organizations/${organizationId}/debts/${debtId}`, { method: 'DELETE' }),

  nudge: ({ organizationId, debtId }: DebtRef): Promise<NudgeResponse> =>
    request(`/organizations/${organizationId}/debts/${debtId}/nudge`, { method: 'POST' }),

  addPayment: ({
    organizationId,
    debtId,
    ...input
  }: AddPaymentInput): Promise<DebtResponse> =>
    upload(
      `/organizations/${organizationId}/debts/${debtId}/payments`,
      paymentBody(input),
    ),

  updatePayment: ({
    organizationId,
    debtId,
    paymentId,
    ...input
  }: UpdatePaymentInput): Promise<DebtResponse> =>
    upload(
      `/organizations/${organizationId}/debts/${debtId}/payments/${paymentId}`,
      paymentBody(input),
      'PATCH',
    ),

  removePayment: ({
    organizationId,
    debtId,
    paymentId,
  }: PaymentRef): Promise<DebtResponse> =>
    request(`/organizations/${organizationId}/debts/${debtId}/payments/${paymentId}`, {
      method: 'DELETE',
    }),

  getReceipt: ({ organizationId, debtId, paymentId }: PaymentRef): Promise<Blob> =>
    download(
      `/organizations/${organizationId}/debts/${debtId}/payments/${paymentId}/receipt`,
    ),

  removeReceipt: ({
    organizationId,
    debtId,
    paymentId,
  }: PaymentRef): Promise<DebtResponse> =>
    request(
      `/organizations/${organizationId}/debts/${debtId}/payments/${paymentId}/receipt`,
      { method: 'DELETE' },
    ),

  addFile: ({ organizationId, debtId, file }: DebtFileInput): Promise<DebtResponse> => {
    const body = new FormData();
    body.append('file', file);
    return upload(`/organizations/${organizationId}/debts/${debtId}/attachments`, body);
  },

  getFile: ({ organizationId, debtId, attachmentId }: DebtFileRef): Promise<Blob> =>
    download(
      `/organizations/${organizationId}/debts/${debtId}/attachments/${attachmentId}`,
    ),

  removeFile: ({
    organizationId,
    debtId,
    attachmentId,
  }: DebtFileRef): Promise<DebtResponse> =>
    request(
      `/organizations/${organizationId}/debts/${debtId}/attachments/${attachmentId}`,
      { method: 'DELETE' },
    ),
};
